import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Coupon } from '../types/coupons';
import { format } from 'date-fns';
import { useCouponAuth } from '../hooks/use-coupon-auth';
import { useClipCoupon } from '../hooks/use-clip-coupon';
import { useClippedCoupons } from '../hooks/use-clipped-coupons';
import { useClipToast } from '../context/ClipToastContext';
import { CouponAuthModal } from './CouponAuthModal';
import { IconCheck, IconLoader4, IconPlus, IconSparkles, IconX } from '@tabler/icons-react';

interface CouponDetailSheetProps {
  coupon: Coupon;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatDate = (date: string | undefined): string | null => {
  if (!date) return null;
  try {
    return format(new Date(date), 'MM/dd/yyyy');
  } catch {
    return null;
  }
};

export const CouponDetailSheet = ({
  coupon,
  open,
  onOpenChange,
}: CouponDetailSheetProps) => {
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const { isAuthenticated } = useCouponAuth();
  const { showClipToast } = useClipToast();
  const clipCouponMutation = useClipCoupon({
    onSuccess: () => showClipToast('success'),
    onError: (error) => showClipToast('error', error.message || 'Unable to clip coupon.'),
  });
  const { data: clippedCoupons = [] } = useClippedCoupons();

  const expirationDate = formatDate(coupon.expirationDate);

  const isClipped = coupon.isClipped || clippedCoupons.some(
    (c) => c.id === coupon.id
  );
  const isLoading = clipCouponMutation.isPending;

  const handleClip = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading) return;

    if (isAuthenticated) {
      const couponId = coupon.id;
      if (couponId) {
        clipCouponMutation.mutate(couponId);
      }
    } else {
      setAuthModalOpen(true);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm p-0 overflow-hidden [&>button:last-child]:hidden">
          {/* Hidden header for accessibility */}
          <DialogHeader className="sr-only">
            <DialogTitle>{coupon.brand || coupon.subtitle || coupon.title}</DialogTitle>
          </DialogHeader>

          {/* Custom close button on left */}
          <DialogClose className="absolute left-4 top-4 z-20 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none">
            <IconX className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>

          {/* Card-like layout identical to CouponCard */}
          <div className="flex flex-col bg-white rounded-2xl overflow-hidden">
            {/* Category Badge */}
            <div className="relative">
              {coupon.category && (
                <Badge className="absolute top-0 right-0 z-10 bg-brand-primary-500 hover:bg-brand-primary-400 text-white text-[10px] font-semibold px-2 py-1 uppercase tracking-wide rounded-none rounded-bl-lg badge-sm">
                  {coupon.category}
                </Badge>
              )}

              {/* Product Image - larger than card */}
              <div className="w-full flex items-center justify-center p-6 pt-10 bg-white">
                {coupon.imageUrl ? (
                  <img
                    src={coupon.imageUrl}
                    alt={coupon.title || 'Coupon'}
                    className="w-[200px] h-[200px] object-contain"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-[200px] h-[200px] bg-gradient-to-br from-primary/10 via-primary/5 to-background flex items-center justify-center rounded">
                    <IconSparkles className="h-12 w-12 text-primary/30" />
                  </div>
                )}
              </div>
            </div>

            {/* Content - matches CouponCard layout */}
            <div className="flex-1 flex flex-col p-5 pt-2 relative">
              <div className="flex-1 space-y-1">
                <p className="text-xl font-bold text-red-500">
                  {coupon.title}
                </p>

                {(coupon.brand || coupon.subtitle) && (
                  <p className="font-bold text-base text-foreground">
                    {coupon.brand || coupon.subtitle}
                  </p>
                )}

                {coupon.description && (
                  <p className="text-sm text-muted-foreground">
                    {coupon.description}
                  </p>
                )}
              </div>

              <div className="flex items-end justify-between mt-4">
                {expirationDate && (
                  <p className="text-xs text-muted-foreground">
                    Offer expires on<br />{expirationDate}
                  </p>
                )}

                <button
                  onClick={handleClip}
                  disabled={isLoading}
                  className={`w-16 h-16 rounded-tl-full flex items-center justify-center pl-2 pt-2 transition-colors absolute bottom-0 right-0 ${
                    isClipped
                      ? 'bg-emerald-500 hover:bg-emerald-600'
                      : 'bg-red-400 hover:bg-red-500'
                  }`}
                >
                  {isLoading ? (
                    <IconLoader4 className="h-6 w-6 text-white animate-spin" />
                  ) : isClipped ? (
                    <IconCheck className="h-7 w-7 text-white" />
                  ) : (
                    <IconPlus className="h-7 w-7 text-white" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CouponAuthModal
        coupon={coupon}
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
      />
    </>
  );
};
